/**
 * Shared `onInvalid` handler for `react-hook-form`'s `handleSubmit`.
 *
 * Pass as the second arg to `form.handleSubmit(onSubmit, scrollToFirstError)`.
 * On validation failure, it finds the first errored field (by schema key), scrolls
 * the matching DOM element into view, and focuses it.
 *
 * Convention: every form input MUST set `id={schemaKey}` so this helper can find it
 * via `document.getElementById(...)`. See `.claude/rules/main-FORM-VALIDATION-PATTERN.md`.
 *
 * For nested paths (e.g. `address.zip`), the helper falls back to dotted-id then
 * to the top-level segment. Form inputs may set `id="address.zip"` or `id="address"`.
 */
import type { FieldErrors, FieldValues } from "react-hook-form";

export function scrollToFirstError<TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues>,
): void {
  if (typeof document === "undefined") return;
  const firstField = Object.keys(errors)[0];
  if (!firstField) return;

  const el =
    document.getElementById(firstField) ??
    document.getElementById(firstField.split(".")[0]) ??
    document.querySelector<HTMLElement>(`[name="${firstField}"]`);

  if (!el) return;

  el.scrollIntoView({ behavior: "smooth", block: "center" });
  // focus deferred so scroll completes; preventScroll keeps focus call from re-scrolling
  if (typeof (el as HTMLElement).focus === "function") {
    (el as HTMLElement).focus({ preventScroll: true });
  }
}
